import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { graphql, useStaticQuery } from 'gatsby';

import { QuestionAndAnswer } from '@components';
import { faqMainData, faqQASpecificData } from '@store/FaqData';
import devProcess from '@images/devProcess.jpg';
import { useDisableBodyScroll } from '@utils/hooks/useDisableBodyScroll';
import { keysToCamel } from '@utils/snakeCaseParser';
import CloseIcon from '@static/images/icons/close.svg';

import * as styles from './FaqContainer.module.scss';

interface FaqQuestion {
	question: string;
	answer: string;
	section: string;
}

export const FaqContainer: React.FC = () => {
	const [imageForPopup, setImageForPopup] = useState(null);
	useDisableBodyScroll(imageForPopup);

	const handleOpenPopup = (e: React.MouseEvent) => {
		const img = e.target as HTMLImageElement;
		setImageForPopup(img.getAttribute('src'));
	};

	const handleClosePopup = () => {
		setImageForPopup(null);
	};

	const stopPropagation = (e: React.MouseEvent) => {
		e.stopPropagation();
	};

	const { directus } = useStaticQuery(graphql`
		query faqDataQuery {
			directus {
				FAQ(
					sort: "id_for_sort"
					filter: { section: { _ncontains: "Careers" } }
				) {
					question
					answer
					section
				}
			}
		}
	`);
	const faqDataQuery: FaqQuestion[] = useMemo(
		() => keysToCamel(directus.FAQ),
		[directus.FAQ]
	);

	const faqQA = useMemo(() => {
		const faqQAData = {};
		faqDataQuery.map((data) => {
			if (!faqQAData[data.section]) {
				faqQAData[data.section] = [];
			}
			faqQAData[data.section].push(data);
		});
		return faqQAData;
	}, []);

	return (
		<>
			<div className={styles.containerFaq}>
				<div
					className={classNames(
						styles.mainBox,
						'container-fluid p-0 pb-3 pt-2'
					)}
				>
					<div
						className={classNames(
							styles.imagePopup,
							imageForPopup
								? 'd-flex justify-content-center align-items-center'
								: 'd-none'
						)}
						onClick={handleClosePopup}
					>
						<div
							className={classNames(
								styles.imageBox,
								'd-flex justify-content-center align-items-center'
							)}
						>
							<img src={imageForPopup} onClick={stopPropagation}></img>
						</div>
						<button
							className={styles.imagePopupCloseBtn}
							onClick={handleClosePopup}
						>
							<img alt="Close icon" src={CloseIcon} />
						</button>
					</div>
					<h1 className="font-600 sub-title pt-5">{faqMainData.title}</h1>
					<div className="pt-2 mb-lg-5">{faqMainData.description}</div>
					{faqQA
						? Object.keys(faqQA).map((section, index) => (
								<div key={`faqSection${index}`}>
									<h2 className="sub-title font-600 mt-4 mt-lg-5">{section}</h2>
									{faqQA[section].map(({ question, answer }, index) => (
										<QuestionAndAnswer
											question={question}
											answer={answer}
											key={`${section}${index}`}
										/>
									))}
									{section === 'Services' ? (
										<>
											<QuestionAndAnswer
												question={faqQASpecificData[0].question}
											>
												<p
													dangerouslySetInnerHTML={{
														__html: faqQASpecificData[0].answer,
													}}
												></p>
											</QuestionAndAnswer>
											<QuestionAndAnswer question="Do you provide other services besides programming to clients?">
												Yes, definitely. Especially given that programming
												itself is only a part of the software development
												process, which we support comprehensively (full stack).
												The entire process is illustrated in the diagram below:
												<img
													src={devProcess}
													className={styles.devProcessImage}
													onClick={handleOpenPopup}
												/>
											</QuestionAndAnswer>
										</>
									) : null}
								</div>
						  ))
						: null}
				</div>
			</div>
		</>
	);
};
